'use client';

import { useRecaptcha } from '@/hooks/useRecaptcha';
import { Link } from '@/i18n/navigation';
import { publicApiFetch } from '@tracedocuments/api';
import {
  AlertTriangle,
  CheckCircle2,
  FileSearch,
  Loader2,
  LogIn,
  Search,
  ShieldCheck,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

export interface VerifyResult {
  isValid: boolean;
  documentName: string | null;
  protectionLevel: string | null;
  fileHash: string | null;
  protectedAt: string | null;
  expiresAt: string | null;
  isExpired: boolean;
  isRevoked: boolean;
}

const SERIAL_PATTERN = /^TD-[A-Z2-9]{6}$/;

export function normalizeSerialInput(raw: string): string {
  const trimmed = raw.trim().toUpperCase();
  if (!trimmed) return '';
  return trimmed.startsWith('TD-') ? trimmed : `TD-${trimmed}`;
}

export function isValidSerialFormat(raw: string): boolean {
  return SERIAL_PATTERN.test(normalizeSerialInput(raw));
}

const LEAK_CHECK_RETURN = '/app/leak-check';

interface VerifyDocumentPanelProps {
  initialCode?: string;
}

export default function VerifyDocumentPanel({ initialCode = '' }: VerifyDocumentPanelProps) {
  const t = useTranslations('verify');
  const { execute: executeRecaptcha, ready: recaptchaReady, isConfigured: recaptchaConfigured } =
    useRecaptcha();
  const [code, setCode] = useState(initialCode);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState('');

  const verifyCode = useCallback(
    async (serialCode: string) => {
      const trimmed = serialCode.trim();
      if (!trimmed) return;

      if (!isValidSerialFormat(trimmed)) {
        setError(t('invalidFormat'));
        setResult(null);
        return;
      }

      setLoading(true);
      setError('');
      setResult(null);

      try {
        if (recaptchaConfigured && !recaptchaReady) {
          setError(t('recaptchaFailed'));
          setLoading(false);
          return;
        }

        const recaptchaToken = await executeRecaptcha('public_verify');
        if (recaptchaConfigured && !recaptchaToken) {
          setError(t('recaptchaFailed'));
          setLoading(false);
          return;
        }

        const res = await publicApiFetch('/api/public/verify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(recaptchaToken ? { 'X-Recaptcha-Token': recaptchaToken } : {}),
          },
          body: JSON.stringify({ serialCode: trimmed }),
        });

        if (res.status === 429) {
          setError(t('rateLimited'));
          return;
        }

        if (res.status === 400) {
          const body = (await res.json().catch(() => null)) as {
            error?: string;
            code?: string;
          } | null;
          if (body?.code === 'INVALID_SERIAL_FORMAT') {
            setError(t('invalidFormat'));
          } else if (body?.error?.toLowerCase().includes('recaptcha')) {
            setError(t('recaptchaFailed'));
          } else {
            setError(body?.error || t('networkError'));
          }
          return;
        }

        if (res.ok) {
          const data = (await res.json()) as VerifyResult;
          setResult(data);
        } else {
          setError(t('networkError'));
        }
      } catch {
        setError(t('networkError'));
      } finally {
        setLoading(false);
      }
    },
    [executeRecaptcha, recaptchaConfigured, recaptchaReady, t]
  );

  useEffect(() => {
    if (!initialCode.trim()) return;
    setCode(initialCode);
    void verifyCode(initialCode);
    // Run once when deep-linked with a serial; verifyCode identity changes are ignored on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void verifyCode(code);
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatus = () => {
    if (!result?.isValid) return null;
    if (result.isRevoked) return { label: t('statusRevoked'), color: 'text-red-500' };
    if (result.isExpired) return { label: t('statusExpired'), color: 'text-amber-500' };
    return { label: t('statusActive'), color: 'text-green-500' };
  };

  return (
    <div className="w-full max-w-lg">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
          <ShieldCheck className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-foreground">{t('title')}</h1>
        <p className="text-muted-foreground mt-1">{t('subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="mb-6">
        <label htmlFor="serial-code" className="block text-sm font-medium text-foreground mb-2">
          {t('inputLabel')}
        </label>
        <div className="flex gap-2">
          <input
            id="serial-code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('inputPlaceholder')}
            className="flex-1 rounded-lg border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 uppercase tracking-wider"
            maxLength={20}
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="submit"
            disabled={loading || !code.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {loading ? t('verifying') : t('button')}
          </button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{t('serialHint')}</p>
      </form>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {result && !result.isValid && (
        <div className="mb-6 rounded-xl border border-border bg-card p-6 text-center">
          <XCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-foreground mb-1">{t('invalid')}</h2>
          <p className="text-sm text-muted-foreground">{t('invalidDescription')}</p>
        </div>
      )}

      {result?.isValid && (
        <div className="mb-6 rounded-xl border border-border bg-card overflow-hidden">
          <div
            className={`px-6 py-4 flex items-center gap-3 ${
              result.isRevoked
                ? 'bg-red-50 dark:bg-red-950/30 border-b border-red-200 dark:border-red-800'
                : result.isExpired
                  ? 'bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800'
                  : 'bg-green-50 dark:bg-green-950/30 border-b border-green-200 dark:border-green-800'
            }`}
          >
            {result.isRevoked ? (
              <XCircle className="w-6 h-6 text-red-500" />
            ) : result.isExpired ? (
              <AlertTriangle className="w-6 h-6 text-amber-500" />
            ) : (
              <CheckCircle2 className="w-6 h-6 text-green-500" />
            )}
            <div>
              <h2 className="font-semibold text-foreground">
                {result.isRevoked ? t('revoked') : result.isExpired ? t('expired') : t('valid')}
              </h2>
              <p className="text-xs text-muted-foreground">
                {result.isRevoked
                  ? t('revokedDescription')
                  : result.isExpired
                    ? t('expiredDescription')
                    : t('validDescription')}
              </p>
            </div>
          </div>

          <div className="px-6 py-4 space-y-3">
            <DetailRow label={t('documentName')} value={result.documentName} />
            <DetailRow label={t('protectionLevel')} value={result.protectionLevel} />
            <DetailRow label={t('status')} value={getStatus()?.label} className={getStatus()?.color} />
            <DetailRow label={t('protectedAt')} value={formatDate(result.protectedAt)} />
            <DetailRow
              label={t('expiresAt')}
              value={result.expiresAt ? formatDate(result.expiresAt) : t('noExpiration')}
            />
            {result.fileHash && (
              <div className="pt-2 border-t border-border">
                <span className="text-xs text-muted-foreground block mb-1">{t('fileHash')}</span>
                <code className="text-xs text-foreground bg-muted/50 px-2 py-1 rounded font-mono break-all">
                  {result.fileHash}
                </code>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card/60 p-5">
        <div className="flex gap-3">
          <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <FileSearch className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-foreground">{t('uploadGuide.title')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('uploadGuide.body')}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                href={`/auth/login?returnUrl=${encodeURIComponent(LEAK_CHECK_RETURN)}`}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <LogIn className="h-4 w-4" />
                {t('uploadGuide.login')}
              </Link>
              <Link
                href={`/auth/register?returnUrl=${encodeURIComponent(LEAK_CHECK_RETURN)}`}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary transition-colors"
              >
                <UserPlus className="h-4 w-4" />
                {t('uploadGuide.register')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  className,
}: {
  label: string;
  value?: string | null;
  className?: string;
}) {
  if (!value) return null;
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium text-foreground ${className ?? ''}`}>{value}</span>
    </div>
  );
}
